import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, filter, map, take } from 'rxjs';

import {
  selectMePermissions,
  selectMePermissionsError,
} from '../Store/rbac-sod/rbac-sod.selectors';
import { PermissionGateService } from './permission-gate.service';

/**
 * Route guard for `/savings-tracker`.
 *
 * The tracker is a shell over tabs that carry different keys, so entry is an
 * ANY-of check rather than a single permission:
 *
 *  - `savings.tracker.view`  — Initiatives, Dashboard, Aging, Reports
 *  - `opportunity.view`      — the Opportunities tab
 *
 * Holding either means there is something to show. Holding neither means every
 * tab would be hidden, and a shell with no tabs is worse than no route at all —
 * so those callers are sent to `/dashboard` rather than landing on an empty
 * page and wondering what broke.
 *
 * `has$` default-denies until the MePermissions slice hydrates, so this waits
 * for the slice to settle (data OR error) before deciding; otherwise every cold
 * navigation would bounce a legitimate user. A load failure stays fail-closed:
 * no permissions proven, no page.
 *
 * This is the security boundary for navigation; the sidebar filter is only a
 * usability gate, and the API's own gates are what actually protect the data.
 */
export const savingsTrackerGuard: CanActivateFn = () => {
  const permissionGate = inject(PermissionGateService);
  const store = inject(Store);
  const router = inject(Router);

  return combineLatest([
    permissionGate.has$('savings.tracker.view'),
    permissionGate.has$('opportunity.view'),
    store.select(selectMePermissions),
    store.select(selectMePermissionsError),
  ]).pipe(
    filter(([, , me, error]) => me != null || error != null),
    take(1),
    map(([canViewTracker, canViewOpportunity]) =>
      canViewTracker || canViewOpportunity ? true : router.parseUrl('dashboard')),
  );
};
